import { Star, CheckCircle2, Send, ThumbsUp, ThumbsDown, Minus } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { PortalData } from '@/types/portalCliente'
import type { PortalClienteState } from '@/types/portalClienteState'
import type { NpsStats } from '@/utils/portalClienteUtils'
import {
  getNpsClassificacao,
  getNpsColor,
  getNpsBgColor,
  NpsScoreBadge,
} from '@/utils/portalClienteUtils'

type Props = { data: PortalData; portal: PortalClienteState; npsStats: NpsStats }

export function PortalClienteNpsSection({ data, portal, npsStats }: Props) {
  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
      <h2 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
        <Star size={14} />
        Pesquisa de Satisfação (NPS)
      </h2>
      {portal.npsEnviado ? (
        <div className="flex items-center gap-3 py-4">
          <CheckCircle2 size={24} className="text-emerald-400" />
          <div>
            <p className="text-sm font-medium text-white">Obrigado pelo seu feedback!</p>
            <p className="text-xs text-white/40">Sua resposta foi registrada com sucesso.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-sm text-white/70 mb-3">De 0 a 10, o quanto você nos recomendaria?</p>
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => portal.setNpsNota(n)}
                  className={cn(
                    'w-10 h-10 rounded-lg text-sm font-bold transition-all border',
                    portal.npsNota === n
                      ? 'bg-accent text-white border-accent scale-110'
                      : 'bg-white/[0.04] text-white/60 border-white/[0.06] hover:border-accent/50',
                    n <= 6
                      ? 'hover:border-red-400/50'
                      : n <= 8
                        ? 'hover:border-yellow-400/50'
                        : 'hover:border-emerald-400/50',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            {portal.npsNota !== null && (
              <p className={cn('text-xs mt-2 font-medium flex items-center gap-1', getNpsColor(portal.npsNota))}>
                {portal.npsNota >= 9 ? (
                  <ThumbsUp size={12} />
                ) : portal.npsNota >= 7 ? (
                  <Minus size={12} />
                ) : (
                  <ThumbsDown size={12} />
                )}
                {getNpsClassificacao(portal.npsNota)}
              </p>
            )}
          </div>
          <textarea
            value={portal.npsComentario}
            onChange={(e) => portal.setNpsComentario(e.target.value)}
            placeholder="Deixe um comentário (opcional)"
            rows={3}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-accent resize-none placeholder:text-white/30"
          />
          <button
            type="button"
            onClick={portal.handleNpsSubmit}
            disabled={portal.npsNota === null || portal.npsSending}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              portal.npsNota !== null
                ? 'bg-accent text-white hover:bg-accent/80'
                : 'bg-white/[0.06] text-white/30 cursor-not-allowed',
            )}
          >
            <Send size={14} />
            {portal.npsSending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      )}
      {data.nps.length > 0 && (
        <div className="pt-3 border-t border-white/[0.06] space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-white/40 uppercase tracking-wider font-medium">Respostas anteriores</p>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-emerald-400">{npsStats.promotores} P</span>
              <span className="text-[11px] text-yellow-400">{npsStats.neutros} N</span>
              <span className="text-[11px] text-red-400">{npsStats.detratores} D</span>
            </div>
          </div>
          {data.nps.map((n) => (
            <div
              key={n.id}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold',
                    getNpsBgColor(n.score),
                    getNpsColor(n.score),
                  )}
                >
                  {n.score}
                </span>
                <div>
                  <NpsScoreBadge score={n.score} />
                  {n.comentario && (
                    <p className="text-xs text-white/60 mt-0.5 max-w-md truncate">{n.comentario}</p>
                  )}
                </div>
              </div>
              <span className="text-[10px] text-white/30">
                {new Date(n.respondidoEm).toLocaleDateString('pt-BR')}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
